import type { ICameraVideoTrack, ILocalTrack } from 'agora-rtc-react';
import AgoraRTC, { IAgoraRTCClient } from 'agora-rtc-react';
import { StudioLogger } from '@studio/logger/StudioLogger';
import { AGORA_ENCODER_CONFIG } from '@studio/constants/VideoQualitySettings';
import {
  BACKGROUND_OPTIONS,
  disableProcessor,
  getVFXConfig,
  transformVideo
} from '../virtualBackgrounds/virtualBackground';

const LOG = new StudioLogger('agoraVideoControl');

interface TransformableCameraTrack {
  transformedVideoTrack?: ICameraVideoTrack | null;
}

export interface VideoInput {
  videoTrack: ICameraVideoTrack | null;
  virtualBackground: BACKGROUND_OPTIONS;
  virtualBackgroundImageUrl: string;
  device: string;
}

/**
 * Initializes a new video input with a camera device
 * Creates a camera video track with the specified device and default encoder configuration
 *
 * @param deviceId - The ID of the camera device to initialize
 * @returns Promise<VideoInput> - A video input object containing the video track, device ID, and default background settings
 * @throws {Error} If video track creation fails
 */
export const initializeVideoInput = async (deviceId: string): Promise<VideoInput> => {
  try {
    const videoTrack = await AgoraRTC.createCameraVideoTrack({
      cameraId: deviceId,
      encoderConfig: AGORA_ENCODER_CONFIG
    });
    const videoInput = {
      videoTrack,
      device: deviceId,
      virtualBackground: BACKGROUND_OPTIONS.NONE,
      virtualBackgroundImageUrl: ''
    };

    LOG.info(`Initialized video input device: ${deviceId}`);
    return videoInput;
  } catch (error) {
    LOG.error(`Failed to initialize video input device ${deviceId}:`, error);
    throw error;
  }
};

/**
 * Changes the camera device for an existing video input
 * If the device is already set, returns the current video input without changes
 * If no video track exists, creates a new video input with the specified device
 *
 * @param videoInput - The current video input object
 * @param deviceId - The ID of the new camera device to switch to
 * @returns Promise<VideoInput> - The updated video input object with the new device
 * @throws {Error} If device switching fails
 */
export const changeVideoInputDevice = async (videoInput: VideoInput, deviceId: string): Promise<VideoInput> => {
  if (videoInput.device === deviceId) {
    LOG.info(`Video input device is already set to: ${deviceId}`);
    return videoInput;
  }
  try {
    if (videoInput.videoTrack === null) {
      LOG.warn('No video track available, now creating a new one with the selected device');
      return initializeVideoInput(deviceId);
    }
    await videoInput.videoTrack.setDevice(deviceId);
    LOG.info(`Changed video input device to: ${deviceId}`);

    // Reapply virtual background if one is set
    if (videoInput.virtualBackground !== BACKGROUND_OPTIONS.NONE) {
      videoInput.videoTrack.unpipe();
      await disableProcessor();
      LOG.info('Device changed while virtual background is applied, reapplying background filter to new device');
      const result = await applyVirtualBackgroundToTrack(
        videoInput.videoTrack,
        videoInput.virtualBackground,
        videoInput.virtualBackgroundImageUrl,
        videoInput.device
      );

      return {
        ...videoInput,
        ...result,
        device: deviceId
      };
    }

    return {
      ...videoInput,
      device: deviceId
    };
  } catch (error) {
    LOG.error(`Failed to change video input device to ${deviceId}:`, error);
    throw error;
  }
};

/**
 * Applies or removes a virtual background effect to a video track
 * @param videoTrack - The video track to apply the background to
 * @param backgroundOption - The type of virtual background to apply (NONE, BLUR, or IMAGE)
 * @param backgroundImageUrl - The URL of the background image (required when backgroundOption is IMAGE)
 * @param deviceId - The ID of the current device
 * @returns Promise<VideoInput> - Video input with updated track and background settings
 */
const applyVirtualBackgroundToTrack = async (
  videoTrack: ICameraVideoTrack,
  backgroundOption: BACKGROUND_OPTIONS,
  backgroundImageUrl: string,
  deviceId: string
): Promise<VideoInput> => {
  const vfxConfig = await getVFXConfig(backgroundOption, backgroundImageUrl);
  if (vfxConfig === undefined) {
    videoTrack.unpipe();
    await disableProcessor();

    return {
      videoTrack,
      device: deviceId,
      virtualBackground: BACKGROUND_OPTIONS.NONE,
      virtualBackgroundImageUrl: ''
    };
  }
  const updatedVideoInput = await transformVideo(videoTrack, vfxConfig, deviceId);
  LOG.info(`Changed user background to option: ${backgroundOption}`);
  return {
    ...updatedVideoInput,
    virtualBackground: backgroundOption,
    virtualBackgroundImageUrl: backgroundImageUrl
  };
};

/**
 * Changes the virtual background for an existing video input
 * Applies blur, image, or removes background effects while maintaining the same camera device
 * If no video track exists, returns the current video input without changes
 *
 * @param videoInput - The current video input object
 * @param backgroundOption - The type of virtual background to apply (NONE, BLUR, or IMAGE)
 * @param backgroundImageUrl - The URL of the background image (required when backgroundOption is IMAGE, defaults to empty string)
 * @returns Promise<VideoInput> - The updated video input object with new background settings
 * @throws {Error} If virtual background configuration or transformation fails
 */
export const changeUserBackground = async (
  videoInput: VideoInput,
  backgroundOption: BACKGROUND_OPTIONS,
  backgroundImageUrl = ''
): Promise<VideoInput> => {
  if (videoInput.videoTrack === null) {
    LOG.warn('No video track available to change background');
    return videoInput;
  }
  if (
    videoInput.virtualBackground === backgroundOption &&
    videoInput.virtualBackgroundImageUrl === backgroundImageUrl
  ) {
    LOG.info(`User background is already set to option: ${backgroundOption}`);
    return videoInput;
  }

  if (videoInput.virtualBackground === BACKGROUND_OPTIONS.NONE && backgroundOption === BACKGROUND_OPTIONS.NONE) {
    LOG.info('User background is already set to NONE');
    return videoInput;
  }

  if (videoInput.virtualBackground === BACKGROUND_OPTIONS.BLUR && backgroundOption === BACKGROUND_OPTIONS.BLUR) {
    LOG.info('User background is already set to BLUR');
    return videoInput;
  }

  try {
    const result = await applyVirtualBackgroundToTrack(
      videoInput.videoTrack,
      backgroundOption,
      backgroundImageUrl,
      videoInput.device
    );
    return {
      ...videoInput,
      ...result
    };
  } catch (error) {
    LOG.error(`Failed to change user background to option ${backgroundOption}:`, error);
    throw error;
  }
};

/**
 * Toggles video for an Agora attendee by enabling/disabling the video track
 * @param videoTrack - The video track to toggle
 * @param enabled - Whether to enable (true) or disable (false) the video
 * @returns {Promise<ICameraVideoTrack | null>} - The video track if enabled, null if disabled or track not provided
 */
export const toggleAgoraVideo = async (
  videoTrack: ICameraVideoTrack | null,
  enabled: boolean,
  client: IAgoraRTCClient
): Promise<ICameraVideoTrack | null> => {
  try {
    LOG.info(`${enabled ? 'Enabling' : 'Disabling'} Agora video`, { enabled });

    const trackToToggle: ICameraVideoTrack | null =
      (videoTrack as TransformableCameraTrack)?.transformedVideoTrack ?? videoTrack;

    if (trackToToggle) {
      await trackToToggle.setEnabled(enabled);
      LOG.info(`Agora video track ${enabled ? 'enabled' : 'disabled'}`);
      enabled ? await client.publish(trackToToggle) : await client.unpublish(trackToToggle);
      return enabled ? trackToToggle : null;
    } else {
      LOG.info('No video track provided');
      return null;
    }
  } catch (error) {
    LOG.error(`Failed to ${enabled ? 'enable' : 'disable'} Agora video`, error);
    throw error;
  }
};

/**
 * Stops a video input track by unpublishing, stopping, and closing it
 * @param client - The Agora RTC client instance
 * @param videoTrack - The video track to stop
 * @param deviceId - Optional device identifier for logging
 */
export async function stopVideoInput(
  client: IAgoraRTCClient,
  videoTrack: ICameraVideoTrack | ILocalTrack | null,
  deviceId?: string
): Promise<void> {
  try {
    if (!client) {
      throw new Error('Agora RTC client is not initialized.');
    }

    if (!videoTrack) {
      LOG.info('No video track provided to stop');
      return;
    }

    if (client.localTracks?.includes(videoTrack)) {
      await client.unpublish(videoTrack);
      LOG.info(`Unpublished video track before stopping for ${deviceId}`);
    }

    videoTrack.stop();
    videoTrack.close();
    LOG.info(`Stopped and closed video track for ${deviceId}`);
  } catch (error) {
    LOG.error(`Failed to stop video input for ${deviceId}:`, error);
    throw error;
  }
}

/**
 * Lists all available video input devices
 * @returns Promise<MediaDeviceInfo[]> - Array of available video input devices
 */
export async function listVideoInputDevices(): Promise<MediaDeviceInfo[]> {
  try {
    const devices = await AgoraRTC.getDevices();
    const videoInputDevices = devices.filter(device => device.kind === 'videoinput');

    return videoInputDevices;
  } catch (error) {
    LOG.error('Failed to list video input devices:', error);
    throw error;
  }
}
